import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Undo2, ShoppingCart } from 'lucide-react';
import { recipes as recipesApi, groceries as groceriesApi } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { toast } from 'sonner';
import GroceriesScreen from './GroceriesScreen';

// Aisle categories for grouping (with expanded keywords matching PrintModal)
const AISLE_CATEGORIES = {
  produce: { order: 1, label: '🥬 Produce', keywords: ['lettuce', 'spinach', 'kale', 'arugula', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'celery', 'onion', 'garlic', 'ginger', 'potato', 'tomato', 'pepper', 'bell pepper', 'jalapeño', 'cucumber', 'zucchini', 'squash', 'eggplant', 'mushroom', 'corn', 'peas', 'asparagus', 'artichoke', 'avocado', 'lemon', 'lime', 'orange', 'apple', 'banana', 'berry', 'berries', 'strawberry', 'blueberry', 'raspberry', 'grape', 'melon', 'watermelon', 'mango', 'pineapple', 'peach', 'pear', 'plum', 'cherry', 'herbs', 'basil', 'cilantro', 'parsley', 'mint', 'dill', 'thyme', 'rosemary', 'sage', 'oregano', 'chives', 'scallion', 'green onion', 'leek', 'shallot', 'fennel', 'radish', 'beet', 'turnip', 'sweet potato'] },
  dairy: { order: 2, label: '🧊 Dairy & Refrigerated', keywords: ['milk', 'cream', 'half-and-half', 'butter', 'margarine', 'cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'ricotta', 'cottage cheese', 'cream cheese', 'sour cream', 'yogurt', 'greek yogurt', 'egg', 'eggs', 'mayonnaise', 'mayo', 'tofu', 'tempeh'] },
  deli: { order: 3, label: '🥪 Deli', keywords: ['salami', 'pepperoni', 'prosciutto', 'ham', 'sliced turkey', 'bologna', 'pastrami', 'mortadella', 'capicola', 'sopressata', 'chorizo', 'hummus'] },
  meat: { order: 4, label: '🥩 Meat & Seafood', keywords: ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'sausage', 'ground beef', 'ground turkey', 'steak', 'roast', 'ribs', 'tenderloin', 'breast', 'thigh', 'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'shrimp', 'prawn', 'crab', 'lobster', 'scallop', 'mussel', 'clam', 'anchovy', 'anchovies'] },
  bakery: { order: 5, label: '🍞 Bakery & Bread', keywords: ['bread', 'loaf', 'baguette', 'roll', 'bun', 'croissant', 'bagel', 'pita', 'tortilla', 'wrap', 'naan', 'flatbread', 'crouton', 'breadcrumb', 'panko'] },
  pantry: { order: 6, label: '🫙 Pantry & Dry Goods', keywords: ['flour', 'sugar', 'brown sugar', 'powdered sugar', 'baking soda', 'baking powder', 'yeast', 'cornstarch', 'cocoa', 'chocolate', 'vanilla', 'extract', 'rice', 'pasta', 'noodle', 'spaghetti', 'penne', 'quinoa', 'couscous', 'oat', 'oatmeal', 'cereal', 'granola', 'nut', 'almond', 'walnut', 'pecan', 'cashew', 'peanut', 'pistachio', 'seed', 'sesame', 'chia', 'lentil', 'chickpea', 'bean', 'black bean', 'kidney bean', 'cannellini', 'canned', 'tomato paste', 'tomato sauce', 'diced tomato', 'coconut milk', 'broth', 'stock', 'bouillon', 'honey', 'maple syrup', 'molasses', 'agave', 'peanut butter', 'almond butter', 'tahini', 'pickle', 'olive', 'caper', 'cornmeal', 'polenta', 'raisin', 'dried fruit'] },
  condiments: { order: 7, label: '🍶 Oils, Vinegars & Condiments', keywords: ['oil', 'olive oil', 'vegetable oil', 'canola oil', 'coconut oil', 'sesame oil', 'avocado oil', 'vinegar', 'balsamic', 'red wine vinegar', 'white wine vinegar', 'apple cider vinegar', 'rice vinegar', 'soy sauce', 'tamari', 'fish sauce', 'worcestershire', 'hot sauce', 'sriracha', 'ketchup', 'mustard', 'dijon', 'relish', 'salsa', 'barbecue sauce', 'teriyaki', 'hoisin', 'oyster sauce', 'miso', 'curry paste', 'pesto', 'dressing'] },
  spices: { order: 8, label: '🧂 Spices & Seasonings', keywords: ['salt', 'pepper', 'spice', 'seasoning', 'cumin', 'coriander', 'paprika', 'smoked paprika', 'cayenne', 'chili powder', 'curry powder', 'garam masala', 'turmeric', 'cinnamon', 'nutmeg', 'clove', 'allspice', 'cardamom', 'ginger powder', 'garlic powder', 'onion powder', 'italian seasoning', 'bay leaf', 'bay leaves', 'saffron'] },
  frozen: { order: 9, label: '❄️ Frozen', keywords: ['frozen', 'ice cream', 'sorbet'] },
  beverages: { order: 10, label: '🥤 Beverages', keywords: ['juice', 'soda', 'wine', 'beer', 'liquor', 'vodka', 'rum', 'whiskey', 'tequila', 'gin', 'coffee', 'tea', 'espresso'] },
  staples: { order: 98, label: '🏠 You May Already Have', keywords: [] },
  other: { order: 99, label: '📦 Other', keywords: [] }
};

// Common staples that most people already have (pantry basics)
const STAPLES = [
  'salt', 'black pepper', 'white pepper', 'kosher salt', 'sea salt',
  'olive oil', 'vegetable oil', 'canola oil', 'cooking spray',
  'flour', 'all-purpose flour', 'bread flour',
  'sugar', 'granulated sugar', 'brown sugar', 'powdered sugar',
  'baking soda', 'baking powder', 'cornstarch',
  'garlic powder', 'onion powder', 'paprika', 'cumin', 'cinnamon', 'oregano', 'basil dried', 'italian seasoning',
  'vanilla extract', 'vanilla',
  'soy sauce', 'worcestershire', 'water'
];

// Words that exclude an ingredient from being a staple (e.g., "bell pepper" is not "pepper")
const STAPLE_EXCLUDERS = ['bell', 'red pepper', 'green pepper', 'yellow pepper', 'orange pepper', 'jalapeño', 'cayenne', 'chili', 'hot'];

// Check if ingredient is a staple
const isStaple = (ingredientName) => {
  const nameLower = ingredientName.toLowerCase();
  const hasExcluder = STAPLE_EXCLUDERS.some(ex => nameLower.includes(ex));
  if (hasExcluder) return false;
  return STAPLES.some(staple => nameLower.includes(staple));
};

// Priority order for checking aisles (processed products before raw ingredients)
// This prevents "apple cider vinegar" from matching "apple" in produce
const AISLE_PRIORITY = ['dairy', 'condiments', 'spices', 'pantry', 'meat', 'bakery', 'frozen', 'beverages', 'produce'];

// Multi-word keywords that should be checked FIRST to avoid partial matches
// e.g., "coconut milk" should match pantry, not dairy (for "milk")
const MULTI_WORD_OVERRIDES = {
  'coconut milk': 'pantry',
  'coconut cream': 'pantry',
  'almond milk': 'pantry',
  'oat milk': 'pantry',
  'soy milk': 'pantry',
  'evaporated milk': 'pantry',
  'condensed milk': 'pantry',
  'ground ginger': 'spices',
  'ginger powder': 'spices',
  'fresh ginger': 'produce',
  'grated ginger': 'produce',
  'apple cider vinegar': 'condiments',
  'rice vinegar': 'condiments',
  'red wine vinegar': 'condiments',
  'balsamic vinegar': 'condiments',
  'cream of tartar': 'spices',
};

// Normalize ingredient name - preserve commas for descriptive text
const normalizeIngredientName = (name) => {
  if (!name) return '';
  let normalized = name.toLowerCase().replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  
  // Group common salt variants together
  if (/\bsalt\b/.test(normalized) && !normalized.includes('salted')) {
    // Normalize all salt types to just "salt" for grouping
    if (/\b(kosher|sea|table|coarse|fine|flaky|himalayan|celtic|rock)\s*salt\b/.test(normalized)) {
      normalized = 'salt';
    }
  }
  
  // Group common pepper variants together
  if (/\bpepper\b/.test(normalized) && !/\b(bell|jalap|cayenne|chili|poblano|serrano|habanero)\b/.test(normalized)) {
    if (/\b(black|white|ground|freshly ground|cracked)\s*pepper\b/.test(normalized)) {
      normalized = 'black pepper';
    }
  }
  
  return normalized;
};

// Get aisle for ingredient - use AI-assigned aisle first, fallback to keyword matching
const getIngredientAisle = (ingredient) => {
  const storedAisle = (ingredient.aisle || '').toLowerCase();
  
  // If AI assigned a valid aisle, use it (including 'staples')
  if (storedAisle && AISLE_CATEGORIES[storedAisle]) {
    return storedAisle;
  }
  
  // Fallback: Check if it's a staple by name
  if (isStaple(ingredient.name || '')) {
    return 'staples';
  }
  
  const nameLower = (ingredient.name || '').toLowerCase();
  
  // Check multi-word overrides FIRST (prevents "coconut milk" → dairy via "milk")
  for (const [phrase, aisle] of Object.entries(MULTI_WORD_OVERRIDES)) {
    if (nameLower.includes(phrase)) {
      return aisle;
    }
  }
  
  // Fallback: Use priority-based keyword matching with word boundaries
  const nameWords = nameLower.split(/\s+/); // Split into words
  
  for (const aisle of AISLE_PRIORITY) {
    const category = AISLE_CATEGORIES[aisle];
    if (category && category.keywords.some(kw => {
      // Check if keyword is a whole word match or at word boundary
      if (nameWords.includes(kw)) return true;
      // For multi-word keywords, check substring
      if (kw.includes(' ') && nameLower.includes(kw)) return true;
      return false;
    })) {
      return aisle;
    }
  }
  
  return 'other';
};

// Generate ingredient key
const getIngredientKey = (name, aisle) => `${normalizeIngredientName(name)}::${aisle}`;

// Format quantity
const formatQuantity = (qty) => {
  if (!qty || qty === 0) return '';
  if (Number.isInteger(qty)) return String(qty);
  const fractions = { 0.25: '¼', 0.33: '⅓', 0.5: '½', 0.67: '⅔', 0.75: '¾', 0.125: '⅛' };
  const whole = Math.floor(qty);
  const decimal = qty - whole;
  for (const [dec, frac] of Object.entries(fractions)) {
    if (Math.abs(decimal - parseFloat(dec)) < 0.05) {
      return whole > 0 ? `${whole}${frac}` : frac;
    }
  }
  return qty.toFixed(1).replace(/\.0$/, '');
};

// Parse quantity
const parseQuantity = (qty) => {
  if (!qty) return 0;
  if (typeof qty === 'number') return qty;
  const str = String(qty).trim();
  
  // Handle slash fractions like "3/4", "1/2", "1 1/2"
  // First check for mixed numbers like "1 1/2"
  const mixedMatch = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixedMatch) {
    const whole = parseInt(mixedMatch[1]);
    const num = parseInt(mixedMatch[2]);
    const denom = parseInt(mixedMatch[3]);
    return whole + (num / denom);
  }
  
  // Check for simple fractions like "3/4"
  const fractionMatch = str.match(/^(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const num = parseInt(fractionMatch[1]);
    const denom = parseInt(fractionMatch[2]);
    return num / denom;
  }
  
  // Handle Unicode fractions
  const fractionMap = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅓': 0.33, '⅔': 0.67, '⅛': 0.125 };
  for (const [frac, val] of Object.entries(fractionMap)) {
    if (str.includes(frac)) {
      const whole = parseInt(str.replace(frac, '').trim()) || 0;
      return whole + val;
    }
  }
  return parseFloat(str) || 0;
};

// Normalize unit
const normalizeUnit = (unit) => {
  if (!unit) return '';
  const u = unit.toLowerCase().trim();
  const unitMap = {
    'tablespoon': 'tbsp', 'tablespoons': 'tbsp', 'tbsps': 'tbsp',
    'teaspoon': 'tsp', 'teaspoons': 'tsp', 'tsps': 'tsp',
    'cup': 'cup', 'cups': 'cup',
    'ounce': 'oz', 'ounces': 'oz',
    'pound': 'lb', 'pounds': 'lb', 'lbs': 'lb',
    'clove': 'clove', 'cloves': 'clove',
    'can': 'can', 'cans': 'can'
  };
  return unitMap[u] || u;
};

const ShoppingListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [checkedItems, setCheckedItems] = useState({});
  const [hideChecked, setHideChecked] = useState(false);
  const [lastAction, setLastAction] = useState(null); // For undo functionality
  const [headerVisible, setHeaderVisible] = useState(true); // iOS-style collapsing header
  const lastScrollY = useRef(0);
  
  // Groceries integration state - simplified, GroceriesScreen handles its own state
  const [groceryItems, setGroceryItems] = useState([]); // Synced from GroceriesScreen for All tab
  const [includeGroceries, setIncludeGroceries] = useState(true);
  const previousRecipeIdsRef = useRef(null); // For auto-clear detection (use ref to avoid infinite loop)
  const uncheckAllGroceriesRef = useRef(null); // Ref to hold uncheck function from GroceriesScreen

  // Callback to receive grocery items from embedded GroceriesScreen
  const handleGroceryItemsChange = useCallback((items) => {
    setGroceryItems(items);
  }, []);

  // Callback to receive uncheck function from GroceriesScreen
  const handleUncheckAllRef = useCallback((uncheckFn) => {
    uncheckAllGroceriesRef.current = uncheckFn;
  }, []);

  // Scroll to top when component mounts (independent scroll from recipe box)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset scroll to top only when switching to groceries tab
  // Other tabs (All, recipe tabs) preserve their scroll position
  useEffect(() => {
    if (activeTab === 'groceries') {
      window.scrollTo(0, 0);
    }
  }, [activeTab]);

  // Helper to navigate back to recipe box with scroll reset
  const navigateBackToRecipeBox = () => {
    window.scrollTo(0, 0);
    navigate('/recipe-box');
  };

  // Generate cache key from recipe IDs (sorted) - same as Mise en Place
  const generateCacheKey = (recipeList) => recipeList.map(r => r.recipe_id).sort().join(',');

  // iOS-style scroll behavior - hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 60) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch initial groceries for the All tab display
  const fetchGroceries = useCallback(async () => {
    try {
      const response = await groceriesApi.get();
      setGroceryItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch groceries:', error);
    }
  }, []);

  // Listen for groceries-changed events from standalone GroceriesScreen only
  // Skip if we're currently on the shopping list (embedded GroceriesScreen uses onItemsChange)
  const isOnShoppingListRef = useRef(true);
  
  useEffect(() => {
    const handleGroceriesChanged = (e) => {
      // Only refetch if the event didn't come from embedded component
      if (!e.detail?.fromEmbedded) {
        fetchGroceries();
      }
    };
    window.addEventListener('groceries-changed', handleGroceriesChanged);
    return () => window.removeEventListener('groceries-changed', handleGroceriesChanged);
  }, [fetchGroceries]);

  // Auto-clear checked groceries when recipe list changes
  const autoClearCheckedGroceries = useCallback(async () => {
    const checkedGroceries = groceryItems.filter(g => g.checked);
    if (checkedGroceries.length > 0) {
      try {
        await groceriesApi.clearChecked();
        // Refetch groceries to get updated state
        await fetchGroceries();
        toast.success(`Moved ${checkedGroceries.length} checked groceries to history`);
      } catch (error) {
        console.error('Failed to auto-clear groceries:', error);
      }
    }
  }, [groceryItems, fetchGroceries]);

  // Load data - use passed recipes directly (RecipeBox already fetches full data)
  useEffect(() => {
    const loadData = async () => {
      // Get passed recipes from navigation state (from RecipeBox)
      const passedRecipes = location.state?.recipes || [];
      // Determine which section this is for (Currently Cooking vs Up Next)
      const listSource = location.state?.source || 'currentMeal';
      const cacheKeyName = listSource === 'upNext' ? 'shoppingListCacheKey_upNext' : 'shoppingListCacheKey';
      const checkedKeyName = listSource === 'upNext' ? 'shoppingListChecked_upNext' : 'shoppingListChecked';
      
      // Fetch groceries for All tab
      fetchGroceries();
      
      if (passedRecipes.length > 0) {
        // Use passed recipes directly - they already have full data including ingredients
        setRecipes(passedRecipes);
        
        // Load checked items for these recipes
        const recipeIds = passedRecipes.map(r => r.recipe_id).sort().join(',');
        const storedCacheKey = localStorage.getItem(cacheKeyName) || '';
        
        // Check if recipe list changed - trigger auto-clear if so
        if (previousRecipeIdsRef.current !== null && previousRecipeIdsRef.current !== recipeIds) {
          // Recipe list changed - auto-clear checked groceries
          autoClearCheckedGroceries();
        }
        previousRecipeIdsRef.current = recipeIds;
        
        if (recipeIds === storedCacheKey) {
          try {
            const saved = localStorage.getItem(checkedKeyName);
            if (saved) setCheckedItems(JSON.parse(saved));
          } catch { /* ignore */ }
        } else {
          // Different recipes - clear checked items for this section
          setCheckedItems({});
          localStorage.removeItem(checkedKeyName);
          localStorage.setItem(cacheKeyName, recipeIds);
        }
        
        setLoading(false);
        return;
      }
      
      // No recipes passed - fetch from API (fallback for Currently Cooking only)
      setLoading(true);
      
      try {
        const response = await recipesApi.getAll({ full: true });
        const allRecipes = response.data.recipes || response.data || [];
        const currentMealRecipes = allRecipes.filter(r => r.current_meal && !r.deleted);
        
        setRecipes(currentMealRecipes);
        
        // Update cache key
        const recipeIds = currentMealRecipes.map(r => r.recipe_id).sort().join(',');
        localStorage.setItem(cacheKeyName, recipeIds);
      } catch (error) {
        console.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]); // Only re-run when navigation state changes, not when callbacks change

  // Determine storage keys based on source
  const listSource = location.state?.source || 'currentMeal';
  const checkedKeyName = listSource === 'upNext' ? 'shoppingListChecked_upNext' : 'shoppingListChecked';

  // Save checked items when they change
  useEffect(() => {
    if (Object.keys(checkedItems).length > 0) {
      try {
        localStorage.setItem(checkedKeyName, JSON.stringify(checkedItems));
      } catch {
        // Quota exceeded - ignore
      }
    }
  }, [checkedItems, checkedKeyName]);

  // Process ingredients
  const { combinedIngredients, ingredientsByRecipe } = useMemo(() => {
    const combined = {};
    const byRecipe = {};
    
    recipes.forEach(recipe => {
      const recipeId = recipe.recipe_id;
      byRecipe[recipeId] = [];
      
      (recipe.ingredients || []).forEach(ing => {
        if (!ing.name) return;
        
        const aisle = getIngredientAisle(ing);
        const key = getIngredientKey(ing.name, aisle);
        const qty = parseQuantity(ing.quantity);
        const normalizedUnit = normalizeUnit(ing.unit);
        const originalUnit = ing.unit || '';
        
        // Only add key once per recipe to avoid duplicates
        if (!byRecipe[recipeId].includes(key)) {
          byRecipe[recipeId].push(key);
        }
        
        if (!combined[key]) {
          combined[key] = {
            key,
            // Keep full ingredient name including sizes in parentheses (e.g., "(15-ounce) can")
            name: ing.name,
            aisle,
            recipeIds: [],
            recipeTitles: {}, // Map of recipeId -> title for display
            qtyByRecipe: {},
            unitByRecipe: {},
            qtyByUnit: {},
            originalUnitByNormalized: {}, // Map normalized unit -> original unit (for display)
            isGrocery: false
          };
        }
        
        if (!combined[key].recipeIds.includes(recipeId)) {
          combined[key].recipeIds.push(recipeId);
          combined[key].recipeTitles[recipeId] = recipe.title;
          combined[key].qtyByRecipe[recipeId] = 0; // Initialize to 0 for accumulation
        }
        
        // Accumulate quantity for same ingredient within same recipe
        combined[key].qtyByRecipe[recipeId] += qty;
        combined[key].unitByRecipe[recipeId] = originalUnit;
        
        if (!combined[key].qtyByUnit[normalizedUnit]) {
          combined[key].qtyByUnit[normalizedUnit] = 0;
          // Store the first original unit we see for this normalized unit
          combined[key].originalUnitByNormalized[normalizedUnit] = originalUnit;
        }
        combined[key].qtyByUnit[normalizedUnit] += qty;
      });
    });
    
    // Add groceries to combined list if includeGroceries is true
    if (includeGroceries && groceryItems.length > 0) {
      byRecipe['groceries'] = [];
      
      groceryItems.forEach(grocery => {
        const key = `grocery_${grocery.id}`;
        byRecipe['groceries'].push(key);
        
        combined[key] = {
          key,
          name: grocery.name,
          aisle: grocery.aisle || 'other',
          recipeIds: ['groceries'],
          recipeTitles: { groceries: 'My Groceries' },
          qtyByRecipe: { groceries: 1 },
          unitByRecipe: { groceries: '' },
          qtyByUnit: { '': 1 },
          originalUnitByNormalized: {},
          isGrocery: true,
          groceryId: grocery.id,
          groceryChecked: grocery.checked
        };
      });
    }
    
    return { combinedIngredients: combined, ingredientsByRecipe: byRecipe };
  }, [recipes, includeGroceries, groceryItems]);

  // Group by aisle and sort by recipe order
  const groupedIngredients = useMemo(() => {
    let ingredientKeys = activeTab === 'all' 
      ? Object.keys(combinedIngredients) 
      : ingredientsByRecipe[activeTab] || [];
    
    // Create recipe order map for sorting (index in recipes array = priority)
    const recipeOrder = {};
    recipes.forEach((r, index) => {
      recipeOrder[r.recipe_id] = index;
    });
    
    const grouped = {};
    ingredientKeys.forEach(key => {
      const ing = combinedIngredients[key];
      if (!ing) return;
      if (!grouped[ing.aisle]) grouped[ing.aisle] = [];
      grouped[ing.aisle].push(ing);
    });
    
    // Sort aisles - predefined categories first by order, then custom aisles alphabetically
    const sortedAisles = Object.keys(grouped).sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      // Find matching predefined category
      const aCategory = Object.entries(AISLE_CATEGORIES).find(([key]) => aLower.includes(key));
      const bCategory = Object.entries(AISLE_CATEGORIES).find(([key]) => bLower.includes(key));
      
      const aOrder = aCategory ? aCategory[1].order : 50;
      const bOrder = bCategory ? bCategory[1].order : 50;
      
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    });
    
    // Sort ingredients within each aisle by recipe order (first recipe they belong to)
    // Groceries sort to the top of each section
    sortedAisles.forEach(aisle => {
      grouped[aisle].sort((a, b) => {
        // Groceries always come first (use -1 for grocery items)
        const aIsGrocery = a.isGrocery || a.recipeIds.includes('groceries');
        const bIsGrocery = b.isGrocery || b.recipeIds.includes('groceries');
        
        if (aIsGrocery && !bIsGrocery) return -1;
        if (!aIsGrocery && bIsGrocery) return 1;
        
        // Both are groceries or both are recipe items - sort by recipe order
        const aFirstRecipe = Math.min(...a.recipeIds.filter(id => id !== 'groceries').map(id => recipeOrder[id] ?? 999));
        const bFirstRecipe = Math.min(...b.recipeIds.filter(id => id !== 'groceries').map(id => recipeOrder[id] ?? 999));
        
        if (aFirstRecipe !== bFirstRecipe) {
          return aFirstRecipe - bFirstRecipe;
        }
        // Same recipe - sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
    });
    
    return { grouped, sortedAisles };
  }, [activeTab, combinedIngredients, ingredientsByRecipe, recipes]);

  // Counts
  const tabCounts = useMemo(() => {
    const counts = { all: Object.keys(combinedIngredients).length };
    Object.entries(ingredientsByRecipe).forEach(([recipeId, keys]) => {
      counts[recipeId] = keys.length;
    });
    return counts;
  }, [combinedIngredients, ingredientsByRecipe]);

  const checkedCount = Object.values(checkedItems).filter(Boolean).length + 
    (includeGroceries ? groceryItems.filter(item => item.checked).length : 0);
  const totalCount = Object.keys(combinedIngredients).length + 
    (includeGroceries ? groceryItems.length : 0);

  // Helper to find matching grocery items for a recipe ingredient
  const findMatchingGroceryItems = (ingredientName) => {
    const normalizedIngName = normalizeIngredientName(ingredientName);
    return groceryItems.filter(g => normalizeIngredientName(g.name) === normalizedIngName);
  };

  // Helper to find matching recipe ingredient keys for a grocery item
  const findMatchingIngredientKeys = (groceryName) => {
    const normalizedGroceryName = normalizeIngredientName(groceryName);
    return Object.keys(combinedIngredients).filter(key => {
      const ing = combinedIngredients[key];
      // Skip grocery items themselves
      if (ing.isGrocery) return false;
      return normalizeIngredientName(ing.name) === normalizedGroceryName;
    });
  };

  // Toggle checked with undo support - also syncs matching grocery items
  const toggleChecked = async (key) => {
    const wasChecked = checkedItems[key];
    const newChecked = !wasChecked;
    setLastAction({ type: 'toggle', key, wasChecked });
    setCheckedItems(prev => ({ ...prev, [key]: newChecked }));
    
    // Sync matching grocery items if groceries are included
    if (includeGroceries) {
      const ingredient = combinedIngredients[key];
      if (ingredient && !ingredient.isGrocery) {
        const matchingGroceries = findMatchingGroceryItems(ingredient.name);
        if (matchingGroceries.length > 0) {
          // Update grocery items that have different checked state
          const groceriesToUpdate = matchingGroceries.filter(g => g.checked !== newChecked);
          if (groceriesToUpdate.length > 0) {
            // Optimistic update
            setGroceryItems(prev => prev.map(item => 
              groceriesToUpdate.some(g => g.id === item.id) ? { ...item, checked: newChecked } : item
            ));
            // Update backend
            try {
              await Promise.all(
                groceriesToUpdate.map(item => groceriesApi.updateItem(item.id, { checked: newChecked }))
              );
            } catch (error) {
              console.error('Failed to sync grocery items:', error);
            }
          }
        }
      }
    }
  };

  // Undo last action - also syncs matching grocery items
  const undoLastAction = async () => {
    if (!lastAction) return;
    
    if (lastAction.type === 'toggle') {
      const restoredChecked = lastAction.wasChecked;
      setCheckedItems(prev => ({ ...prev, [lastAction.key]: restoredChecked }));
      
      // Sync matching grocery items if groceries are included
      if (includeGroceries) {
        const ingredient = combinedIngredients[lastAction.key];
        if (ingredient && !ingredient.isGrocery) {
          const matchingGroceries = findMatchingGroceryItems(ingredient.name);
          if (matchingGroceries.length > 0) {
            const groceriesToUpdate = matchingGroceries.filter(g => g.checked !== restoredChecked);
            if (groceriesToUpdate.length > 0) {
              // Optimistic update
              setGroceryItems(prev => prev.map(item => 
                groceriesToUpdate.some(g => g.id === item.id) ? { ...item, checked: restoredChecked } : item
              ));
              // Update backend
              try {
                await Promise.all(
                  groceriesToUpdate.map(item => groceriesApi.updateItem(item.id, { checked: restoredChecked }))
                );
              } catch (error) {
                console.error('Failed to sync grocery items on undo:', error);
              }
            }
          }
        }
      }
    } else if (lastAction.type === 'clear') {
      setCheckedItems(lastAction.previousState);
    }
    setLastAction(null);
  };

  // Uncheck all - recipe items and grocery items
  const clearAllChecked = async () => {
    setLastAction({ type: 'clear', previousState: { ...checkedItems } });
    setCheckedItems({});
    localStorage.removeItem(checkedKeyName);
    
    // Also uncheck groceries if included
    if (includeGroceries) {
      const checkedGroceries = groceryItems.filter(g => g.checked);
      if (checkedGroceries.length > 0) {
        // Optimistic update
        setGroceryItems(prev => prev.map(item => ({ ...item, checked: false })));
        
        // Update backend
        try {
          await Promise.all(
            checkedGroceries.map(item => groceriesApi.updateItem(item.id, { checked: false }))
          );
        } catch (error) {
          // Revert on error - refetch
          fetchGroceries();
        }
      }
    }
  };

  // Clear undo after 5 seconds
  useEffect(() => {
    if (lastAction) {
      const timer = setTimeout(() => setLastAction(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [lastAction]);

  // Handler for toggling grocery items from the All tab - also syncs matching recipe ingredients
  const handleToggleGroceryItem = async (itemId, currentChecked) => {
    const newChecked = !currentChecked;
    
    // Find the grocery item to get its name for syncing
    const groceryItem = groceryItems.find(g => g.id === itemId);
    
    // Optimistic update
    setGroceryItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: newChecked } : item
    ));
    
    // Sync matching recipe ingredients
    if (groceryItem) {
      const matchingKeys = findMatchingIngredientKeys(groceryItem.name);
      if (matchingKeys.length > 0) {
        // Update recipe ingredients that have different checked state
        const keysToUpdate = matchingKeys.filter(key => (checkedItems[key] || false) !== newChecked);
        if (keysToUpdate.length > 0) {
          setCheckedItems(prev => {
            const updated = { ...prev };
            keysToUpdate.forEach(key => {
              updated[key] = newChecked;
            });
            return updated;
          });
        }
      }
    }
    
    try {
      await groceriesApi.updateItem(itemId, { checked: newChecked });
    } catch (error) {
      // Revert on error
      setGroceryItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, checked: currentChecked } : item
      ));
      toast.error('Failed to update item');
    }
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading shopping list..." fullScreen />
    );
  }

  if (recipes.length === 0) {
    return (
      <div className="min-h-screen bg-amber-50">
        <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <button onClick={navigateBackToRecipeBox} className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Shopping List</h1>
            </div>
          </div>
        </header>
        <EmptyState
          icon="🛒"
          title="No recipes in Currently Cooking"
          description="Add recipes to Currently Cooking to generate a shopping list."
          action={
            <button 
              onClick={navigateBackToRecipeBox} 
              className="px-6 py-3 bg-amber-600 text-white rounded-full font-medium hover:bg-amber-700 transition-colors"
            >
              Go to Recipe Box
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 pb-20">
      {/* iOS-style collapsing header */}
      <header 
        className={`bg-white/80 backdrop-blur-sm shadow-sm fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={navigateBackToRecipeBox} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Shopping List</h1>
                <p className="text-sm text-gray-500">{checkedCount} of {totalCount} items checked</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Show/Hide checked toggle */}
              {checkedCount > 0 && (
                <button 
                  onClick={() => setHideChecked(!hideChecked)} 
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {hideChecked ? 'Show' : 'Hide'}
                </button>
              )}
              {/* Uncheck All button */}
              {checkedCount > 0 && (
                <button onClick={clearAllChecked} className="text-sm text-amber-600 hover:text-amber-700 font-medium">
                  Uncheck All
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-[72px]" />

      {/* Recipe tabs - always sticky at top when header hides */}
      <div className={`sticky z-30 bg-white/95 backdrop-blur-sm border-b transition-all duration-300 ${
        headerVisible ? 'top-[72px]' : 'top-0'
      }`}>
        <div className="max-w-4xl mx-auto px-4">
          {/* Include Groceries checkbox / Add Groceries link */}
          <div className="py-2 border-b border-gray-100">
            {groceryItems.length > 0 ? (
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={includeGroceries}
                  onChange={(e) => {
                    setIncludeGroceries(e.target.checked);
                    if (!e.target.checked && activeTab === 'groceries') {
                      setActiveTab('all');
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-gray-700">Include My Groceries</span>
                <span className="text-gray-400">({groceryItems.length})</span>
              </label>
            ) : (
              <button
                onClick={() => {
                  setIncludeGroceries(true);
                  setActiveTab('groceries');
                }}
                className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700"
              >
                <ShoppingCart size={16} />
                <span>Add Groceries</span>
              </button>
            )}
          </div>
          
          {/* Pill tabs */}
          <div className="flex gap-2 py-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === 'all' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All <span className="opacity-75">({tabCounts.all})</span>
            </button>
            {/* Groceries pill tab - shown right after All when includeGroceries is checked */}
            {includeGroceries && (
              <button
                onClick={() => setActiveTab('groceries')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'groceries' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Groceries <span className="opacity-75">({groceryItems.length})</span>
              </button>
            )}
            {recipes.map(recipe => (
              <button
                key={recipe.recipe_id}
                onClick={() => setActiveTab(recipe.recipe_id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === recipe.recipe_id ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {recipe.title.length > 20 ? recipe.title.substring(0, 20) + '...' : recipe.title}
                <span className="opacity-75 ml-1">({tabCounts[recipe.recipe_id] || 0})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Groceries tab content - uses the shared GroceriesScreen component */}
      {activeTab === 'groceries' ? (
        <GroceriesScreen 
          key="embedded-groceries"
          embedded={true} 
          onItemsChange={handleGroceryItemsChange}
          headerVisible={headerVisible}
          hideChecked={hideChecked}
          onUncheckAll={handleUncheckAllRef}
        />
      ) : (
        /* Regular ingredient tabs content */
        <div className="max-w-4xl mx-auto px-4 py-4">
        {groupedIngredients.sortedAisles.map(aisle => {
          let items = groupedIngredients.grouped[aisle];
          
          // Filter out checked items if hideChecked is enabled
          if (hideChecked) {
            items = items.filter(ing => {
              // For grocery items, check groceryChecked
              if (ing.isGrocery) {
                return !ing.groceryChecked;
              }
              // For recipe ingredients, check checkedItems
              return !checkedItems[ing.key];
            });
          }
          
          // Skip empty aisles after filtering
          if (items.length === 0) return null;
          
          // Use the configured label if it exists, otherwise use aisle name directly
          const aisleLower = aisle.toLowerCase();
          const aisleConfig = Object.entries(AISLE_CATEGORIES).find(([key]) => aisleLower === key);
          const aisleLabel = aisleConfig ? aisleConfig[1].label : aisle;
          
          return (
            <div key={aisle} className="mb-6">
              <h3 className={`text-base font-semibold text-gray-800 mb-2 sticky bg-gray-100/80 backdrop-blur-sm rounded-md py-2 px-3 z-20 transition-all duration-300 ${
                headerVisible ? 'top-[120px]' : 'top-[48px]'
              }`}>
                {aisleLabel}
              </h3>
              <div className="space-y-0.5">
                {items.map(ing => {
                  let displayText = '';
                  if (activeTab === 'all') {
                    const parts = Object.entries(ing.qtyByUnit)
                      .filter(([, qty]) => qty > 0)
                      .map(([normalizedUnit, qty]) => {
                        const formattedQty = formatQuantity(qty);
                        // Use original unit for display if available
                        const displayUnit = ing.originalUnitByNormalized?.[normalizedUnit] || normalizedUnit;
                        return displayUnit ? `${formattedQty} ${displayUnit}` : formattedQty;
                      });
                    displayText = parts.length > 0 ? parts.join(' + ') + ' ' : '';
                  } else {
                    const qty = ing.qtyByRecipe[activeTab] || 0;
                    const unit = ing.unitByRecipe[activeTab] || '';
                    const formattedQty = formatQuantity(qty);
                    if (formattedQty) {
                      displayText = unit ? `${formattedQty} ${unit} ` : `${formattedQty} `;
                    }
                  }
                  
                  // Get recipe titles for display (only in "All" tab)
                  const recipeNames = activeTab === 'all' && ing.recipeIds.length > 0
                    ? ing.recipeIds.map(id => ing.recipeTitles[id]).filter(Boolean)
                    : [];
                  
                  // Handle item click - grocery items need special handling
                  const handleItemClick = () => {
                    if (ing.isGrocery) {
                      // Toggle grocery checked state via API
                      handleToggleGroceryItem(ing.groceryId, ing.groceryChecked);
                    } else {
                      // Regular ingredient - toggle local checked state
                      toggleChecked(ing.key);
                    }
                  };
                  
                  // For grocery items, use their checked state
                  const isChecked = ing.isGrocery ? ing.groceryChecked : (checkedItems[ing.key] || false);
                  
                  return (
                    <button
                      key={ing.key}
                      onClick={handleItemClick}
                      className="w-full flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-white/50 transition-colors text-left group"
                    >
                      <div className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors mt-0.5 ${
                        isChecked ? 'bg-amber-600 border-amber-600' : 'border-gray-300 group-hover:border-amber-400'
                      }`}>
                        {isChecked && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-grow min-w-0">
                        <span className={`block ${isChecked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                          <span className="font-medium">{displayText}</span>
                          {ing.name}
                        </span>
                        {/* Show recipe names in small font */}
                        {recipeNames.length > 0 && (
                          <span className={`text-xs ${isChecked ? 'text-gray-300' : 'text-gray-400'}`}>
                            {recipeNames.join(', ')}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Undo button - appears after accidental action */}
      {lastAction && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={undoLastAction}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
          >
            <Undo2 size={16} />
            <span>Undo</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ShoppingListPage;
